import { CircularProgress, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { RootState } from "config/store";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "react-query";
import { useSelector } from "react-redux";
import { getDepartmentList } from "shared/network/department.api";
import { Department } from "shared/types";
import displayAddress from "shared/util/displayAddress";
import { useDebouncedCallback } from "use-debounce";
import { nextPageFunction } from "views/Reminder/ReminderForm";
import { CashRegisterFormValues } from "./CashRegisterForm";

type Props = {
  defaultValue?: Department;
};

const CashRegisterDepartmentSelect = ({ defaultValue }: Props) => {
  const { t } = useTranslation();
  const form = useFormContext<CashRegisterFormValues>();
  const { control } = form;
  const tenantId = useSelector(
    (state: RootState) => state.authentication?.selectedRelTenant?.tenant?.id,
  );
  const [departmentSearch, setDepartmentSearch] = useState("");

  const departmentListQuery = useInfiniteQuery(
    [`departmentListPageableQuery`, tenantId, departmentSearch],
    async ({ pageParam = 0 }) => {
      const { data } = await getDepartmentList(
        pageParam,
        10,
        tenantId,
        departmentSearch ? `name:${departmentSearch};` : "",
      );
      return data;
    },
    { getNextPageParam: lastGroup => nextPageFunction(lastGroup) },
  );

  const flatPages = departmentListQuery.data?.pages.map(page => page.page?.content).flat();
  const hasNextPage =
    departmentListQuery.data &&
    flatPages &&
    departmentListQuery.data?.pages?.[0]?.page?.totalElements > flatPages?.length;

  const handleSearch = useDebouncedCallback((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setDepartmentSearch(value);
    }
  }, 500);

  return (
    <Controller
      control={control}
      name="department"
      defaultValue={defaultValue || null}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...field}
          onChange={(_, value) => {
            field.onChange(value);
            handleSearch("");
          }}
          onInputChange={(event, newInputValue) => {
            handleSearch(newInputValue);
          }}
          options={flatPages || []}
          getOptionLabel={(option: Department) =>
            option.name + ", " + displayAddress(option.address) || ""
          }
          getOptionSelected={option => option.id === field.value?.id}
          renderInput={params => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(departmentListQuery.isFetchingNextPage || departmentListQuery.isFetching) && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true, required: true }}
              SelectProps={{ displayEmpty: true }}
              label={t("department.formValues.address.department").toString()}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
          ListboxProps={{
            id: "tool-autocomplete-listbox",
            style: { maxHeight: 300, overflow: "auto" },
            onScroll: (event: React.UIEvent<HTMLUListElement>) => {
              const listboxNode = event.currentTarget;
              if (
                listboxNode.scrollTop + listboxNode.clientHeight >=
                listboxNode.scrollHeight - 10
              ) {
                if (hasNextPage && !departmentListQuery.isFetchingNextPage) {
                  departmentListQuery.fetchNextPage();
                }
              }
            },
          }}
        />
      )}
    />
  );
};

export default CashRegisterDepartmentSelect;
